import { CreditCard, Calendar, FileText, CheckSquare, LayoutDashboard, Timer } from "lucide-react";
import { StatCard } from "../../../components/ui/StatCard";
import { useAuth } from "../../../hooks/useAuth";
import { useEmployeeSummary } from "../../../hooks/useSummary";
import { formatCurrency, formatPeriod } from "../../../utils/format";

export default function EmployeeDashboard() {
  const { user, hasPageAccess } = useAuth();
  const { data: summary, isLoading } = useEmployeeSummary();

  const firstName = user?.firstName ?? user?.username ?? "there";

  const loanValue = isLoading
    ? undefined
    : summary?.loan.hasActiveLoan
      ? formatCurrency(summary.loan.activeLoanAmount)
      : summary?.loan.pendingCount
        ? `${summary.loan.pendingCount} Pending`
        : "No Active Loan";

  const loanSubtitle = isLoading
    ? undefined
    : summary?.loan.hasActiveLoan
      ? "Active loan — deducted from payslip"
      : summary?.loan.pendingCount
        ? "Awaiting admin approval"
        : "Apply for a loan anytime";

  const leaveValue = isLoading
    ? undefined
    : summary?.leave.pendingCount
      ? `${summary.leave.pendingCount} Pending`
      : "No Pending Leave";

  const leaveSubtitle = isLoading
    ? undefined
    : summary?.leave.pendingCount
      ? "Awaiting admin approval"
      : "No pending leave requests";

  const payslipValue = isLoading
    ? undefined
    : summary?.payslip.latest
      ? formatCurrency(summary.payslip.latest.netPay)
      : "No payslip yet";

  const payslipSubtitle = isLoading
    ? undefined
    : summary?.payslip.latest
      ? `${formatPeriod(summary.payslip.latest.period)} · ${summary.payslip.latest.status}`
      : "Payslips will appear here";

  const tasksValue = isLoading
    ? undefined
    : String(summary?.tasks.total ?? 0);

  const tasksSubtitle = isLoading
    ? undefined
    : summary?.tasks.inProgress
      ? `${summary.tasks.inProgress} In Progress · ${summary.tasks.todo} To Do`
      : "No tasks assigned yet";

  const workAdjPending = summary?.workAdjustments.pendingCount ?? 0;
  const workAdjValue = isLoading ? undefined : String(workAdjPending);
  const workAdjSubtitle = isLoading
    ? undefined
    : workAdjPending > 0
      ? `${workAdjPending} awaiting admin review`
      : "No pending claims";

  const cards = [
    hasPageAccess("employee:loans") && (
      <StatCard
        key="loans"
        title="Loan"
        value={loanValue}
        subtitle={loanSubtitle}
        icon={<CreditCard className="h-6 w-6" />}
        iconBgClass="bg-[#eff3ff] text-[#2447d9]"
        viewAllHref="/employee/loans"
        loading={isLoading}
      />
    ),
    hasPageAccess("employee:leave") && (
      <StatCard
        key="leave"
        title="Leave Requests"
        value={leaveValue}
        subtitle={leaveSubtitle}
        icon={<Calendar className="h-6 w-6" />}
        iconBgClass="bg-[#f0fdf4] text-[#16a34a]"
        viewAllHref="/employee/leave"
        loading={isLoading}
      />
    ),
    hasPageAccess("employee:payslip") && (
      <StatCard
        key="payslip"
        title="Latest Payslip"
        value={payslipValue}
        subtitle={payslipSubtitle}
        icon={<FileText className="h-6 w-6" />}
        iconBgClass="bg-[#f5f3ff] text-[#7c3aed]"
        viewAllHref="/employee/payslip"
        loading={isLoading}
      />
    ),
    hasPageAccess("employee:tasks") && (
      <StatCard
        key="tasks"
        title="Tasks Assigned"
        value={tasksValue}
        subtitle={tasksSubtitle}
        icon={<CheckSquare className="h-6 w-6" />}
        iconBgClass="bg-[#fffbeb] text-[#d97706]"
        viewAllHref="/employee/tasks"
        loading={isLoading}
      />
    ),
    hasPageAccess("employee:work-adjustments") && (
      <StatCard
        key="work-adjustments"
        title="Work Time Claims"
        value={workAdjValue}
        subtitle={workAdjSubtitle}
        icon={<Timer className="h-6 w-6" />}
        iconBgClass={workAdjPending > 0 ? "bg-[#eff3ff] text-[#2447d9]" : "bg-[#f1f5f9] text-[#94a3b8]"}
        viewAllHref="/employee/work-adjustments"
        loading={isLoading}
      />
    ),
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0f172a]">Employee Dashboard</h1>
        <p className="mt-1 text-sm text-[#64748b]">
          Welcome back,{" "}
          <span className="font-medium text-[#2447d9]">{firstName}</span>! Here's your HR snapshot.
        </p>
      </div>

      {/* Stat cards */}
      {cards.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#e2e8f0] bg-[#f8fafc] py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eff3ff] mb-4">
            <LayoutDashboard className="h-7 w-7 text-[#2447d9]" />
          </div>
          <p className="text-base font-semibold text-[#0f172a]">Nothing to show here</p>
          <p className="mt-1 text-sm text-[#64748b] max-w-xs">
            Your current role doesn't grant access to any dashboard features. Contact your admin to update your access.
          </p>
        </div>
      )}
    </div>
  );
}
